import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Clock, MapPin, Tag, BookmarkSimple, Share, ArrowSquareOut, Sparkle, SpinnerGap } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { GrantTrackPrompt } from "@/components/GrantTrackPrompt";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function ArticlePage() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [aiSummary, setAiSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user, saveArticle, unsaveArticle } = useAuth();

  const isSaved = user?.saved_articles?.includes(id);

  const fetchArticle = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/articles/${id}`);
      setArticle(response.data);
    } catch (error) {
      console.error("Error fetching article:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchAiSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const response = await axios.get(`${API}/articles/${id}/summary`);
      setAiSummary(response.data);
    } catch (error) {
      console.error("Error fetching AI summary:", error);
    } finally {
      setLoadingSummary(false);
    }
  }, [id]);

  useEffect(() => {
    fetchArticle();
  }, [fetchArticle]);

  useEffect(() => {
    if (article && !article.link_only && article.content) {
      fetchAiSummary();
    }
  }, [article, fetchAiSummary]);

  const handleSave = async () => {
    if (!user) {
      toast.error("Please sign in to save articles");
      return;
    }

    try {
      if (isSaved) {
        await unsaveArticle(id);
        toast.success("Article removed from saved");
      } else {
        await saveArticle(id);
        toast.success("Article saved");
      }
    } catch (error) {
      toast.error("Failed to save article");
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: article.title,
          text: article.summary || article.title,
          url: window.location.href
        });
      } catch (error) {
        // User cancelled or error
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const regionLabels = {
    ireland: "Ireland",
    uk: "United Kingdom",
    usa: "United States",
    china: "China",
    india: "India",
    brazil: "Brazil",
    global: "Global"
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF1E5]">
        <Header />
        <main className="max-w-[900px] mx-auto px-4 py-12">
          <div className="animate-pulse">
            <div className="h-8 bg-[#E5E7EB] w-1/4 mb-4"></div>
            <div className="h-12 bg-[#E5E7EB] w-3/4 mb-6"></div>
            <div className="h-64 bg-[#E5E7EB] mb-6"></div>
            <div className="space-y-4">
              <div className="h-4 bg-[#E5E7EB] w-full"></div>
              <div className="h-4 bg-[#E5E7EB] w-full"></div>
              <div className="h-4 bg-[#E5E7EB] w-3/4"></div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#FFF1E5]">
        <SEO title="Article Not Found" description="This article has been removed from Farmwatch." noindex />
        <Header />
        <main className="max-w-[900px] mx-auto px-4 py-12 text-center">
          <h1 className="font-serif text-3xl text-[#33302E] mb-4">Article Not Found</h1>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#0F766E] hover:underline"
          >
            <ArrowLeft size={20} />
            Back to Home
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const linkOnly = !!article && (article.link_only || (!article.content && !!article.url));

  return (
    <div className="min-h-screen bg-[#FFF1E5]">
      {/* Dynamic SEO for article */}
      <SEO 
        title={article.title}
        description={article.summary || article.content?.substring(0, 160) || `${article.title} — reported by ${article.source}. Headline and link via Farmwatch.`}
        image={linkOnly ? undefined : article.image_url}
        url={`/article/${article.id}`}
        type="article"
        publishedTime={article.published_at}
        modifiedTime={article.scraped_at}
        author={article.source}
        section={article.category}
        tags={[article.category, article.region, "farming", "agriculture", "news"]}
      />
      
      <Header />
      
      {/* Article with Sidebar Layout */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <main className="flex-1 max-w-[800px]">
        {/* Back Link */}
        <Link
          to="/"
          data-testid="back-to-home"
          className="inline-flex items-center gap-2 text-[#6B6661] hover:text-[#0F766E] mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="font-sans text-sm">Back to News</span>
        </Link>

        {/* Article Header */}
        <header className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-2 py-1 bg-[#E5E7EB] text-[#6B6661] text-xs font-mono uppercase tracking-wider">
              {article.category}
            </span>
            <span className="px-2 py-1 bg-[#0F766E] text-white text-xs font-mono uppercase tracking-wider">
              {regionLabels[article.region] || article.region}
            </span>
          </div>
          
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#33302E] leading-tight mb-6">
            {article.title}
          </h1>

          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-4 text-sm text-[#6B6661] font-sans">
              <div className="flex items-center gap-1">
                <Clock size={16} />
                <span>{formatDate(article.published_at)}</span>
              </div>
              <div className="flex items-center gap-1">
                <MapPin size={16} />
                <span>{regionLabels[article.region] || article.region}</span>
              </div>
              <div className="flex items-center gap-1">
                <Tag size={16} />
                <span>{article.source}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleSave}
                data-testid="save-article-btn"
                className={`flex items-center gap-2 px-4 py-2 border transition-colors ${
                  isSaved
                    ? "bg-[#0F766E] border-[#0F766E] text-white"
                    : "border-[#E0D4C8] text-[#6B6661] hover:border-[#0F766E] hover:text-[#0F766E]"
                }`}
              >
                <BookmarkSimple size={18} weight={isSaved ? "fill" : "regular"} />
                <span className="text-sm font-sans">{isSaved ? "Saved" : "Save"}</span>
              </button>
              <button
                onClick={handleShare}
                data-testid="share-article-btn"
                className="flex items-center gap-2 px-4 py-2 border border-[#E0D4C8] text-[#6B6661] hover:border-[#0F766E] hover:text-[#0F766E] transition-colors"
              >
                <Share size={18} />
                <span className="text-sm font-sans">Share</span>
              </button>
            </div>
          </div>
        </header>

        {/* Note: Images removed to avoid copyright issues with scraped content */}

        {linkOnly ? (
          <section className="bg-white border-2 border-[#0F766E] p-6 sm:p-8" data-testid="link-only-notice">
            <p className="font-mono text-[10px] uppercase tracking-wider text-[#0F766E] mb-3">Headline &amp; link only</p>
            <p className="font-serif text-xl sm:text-2xl text-[#33302E] leading-snug mb-3">
              This story is reported by <span className="font-semibold text-[#0F766E]">{article.source}</span>.
            </p>
            <p className="font-sans text-sm text-[#6B6661] mb-6">
              Farmwatch links to the original report rather than reproducing it. The full article, photographs and author byline are available at the source.
            </p>
            {article.url && (
              <a
                href={article.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#0F766E] text-white font-semibold hover:bg-[#064E3B] transition-colors"
                data-testid="read-full-story-btn"
              >
                <span>Read the full story at {article.source}</span>
                <ArrowSquareOut size={18} weight="bold" />
              </a>
            )}
          </section>
        ) : (
        <>
        {/* Article Content */}
        <article className="prose prose-lg max-w-none">
          <div className="font-sans text-[#33302E] leading-relaxed">
            {article.url && (
              <p className="font-mono text-[10px] uppercase tracking-wider text-[#0F766E] mb-3" data-testid="excerpt-label">
                Excerpt — via {article.source}
              </p>
            )}
            {(article.content || "").split('\n').map((paragraph, index) => {
              const trimmed = paragraph.trim();
              
              // Skip empty lines
              if (!trimmed) {
                return <div key={index} className="h-2" />;
              }
              
              // List item patterns - short lines that are part of bullet lists
              const isListItem = (
                trimmed.match(/^(\d+\.|•|-)/) ||
                (trimmed.length < 55 && 
                 !trimmed.match(/[.!?]$/) && 
                 (trimmed.match(/^(The world's|A major|One of the|Land reform|Investment in|Improved technology|Lower land|Large-scale|Currency|Government|Greater efficiency|Lower per-unit|Faster adoption|Soybeans are|Followed by|Expansion of|Development of|The Middle|North Africa|Parts of|Production levels|Export policies|Logistics|Traditional exporters|Import-dependent|Global pricing|Smaller farm|Higher regulatory|Greater production|High-quality|Traceability|Value-added|New land|Technology adoption|Global demand)/i) ||
                  trimmed.match(/^(can quickly|Changes in|A drought|A fertiliser|A geopolitical)/i)))
              );
              
              // Section headers - distinctive titles that introduce new sections
              const isHeader = (
                !isListItem &&
                trimmed.length > 15 &&
                trimmed.length < 70 &&
                !trimmed.match(/[.!?,;]$/) &&
                (
                  trimmed.match(/^(From |The Power|The Black|Climate and|Infrastructure|Influence on|Cost Competitiveness|What It Means|A System|Final Thought|Where |Why It|A Highly|The Four)/i) ||
                  trimmed.match(/^[A-Z][a-z]+ (and |in Context|Under Pressure|Fits In|Still Expanding)$/i)
                )
              );
              
              if (isHeader) {
                return (
                  <h3 key={index} className="font-serif text-xl sm:text-2xl font-semibold text-[#33302E] mt-10 mb-4">
                    {trimmed}
                  </h3>
                );
              }
              
              if (isListItem) {
                return (
                  <p key={index} className="text-[#374151] leading-relaxed pl-6 my-1 before:content-['•'] before:absolute before:-ml-4 before:text-[#0F766E] relative">
                    {trimmed.replace(/^(\d+\.|•|-)\s*/, '')}
                  </p>
                );
              }
              
              return (
                <p key={index} className="text-[#374151] leading-relaxed mb-4">
                  {trimmed}
                </p>
              );
            })}
          </div>
        </article>

        {/* In-Content Advertisement */}

        {/* FarmWatch Analysis Section */}
        <div className="mt-10 mb-8 p-6 bg-white border-2 border-[#0F766E] rounded-lg shadow-sm">
          <div className="flex items-center gap-3 mb-5 pb-4 border-b border-[#E0D4C8]">
            <div className="p-2 bg-[#0F766E] rounded">
              <Sparkle size={18} weight="fill" className="text-white" />
            </div>
            <div>
              <h3 className="font-serif text-xl text-[#33302E] font-semibold">FarmWatch Analysis</h3>
              <p className="text-xs text-[#0F766E] font-mono uppercase tracking-wider">Editorial Commentary</p>
            </div>
          </div>
          
          {loadingSummary ? (
            <div className="flex items-center gap-3 text-[#0F766E]">
              <SpinnerGap size={20} className="animate-spin" />
              <span className="font-sans text-sm">Our editors are preparing analysis...</span>
            </div>
          ) : aiSummary?.summary ? (
            <div className="space-y-4">
              {aiSummary.summary.split('\n\n').map((paragraph, idx) => (
                <p key={idx} className="font-sans text-[#33302E] leading-relaxed text-[15px]">
                  {paragraph}
                </p>
              ))}
              {aiSummary?.ai_generated && (
                <p className="text-xs text-[#6B6661] mt-4 pt-4 border-t border-[#E0D4C8] italic">
                  Analysis generated by FarmWatch editorial intelligence
                </p>
              )}
            </div>
          ) : (
            <p className="font-sans text-[#33302E] leading-relaxed">
              {article.summary}
            </p>
          )}
        </div>
        </>
        )}

        {/* Grant scheme tracking prompt (grant-related articles only) */}
        <GrantTrackPrompt article={article} />

        {/* Source Attribution & Full Story Link */}
        {!linkOnly && (
        <div className="mt-8 pt-8 border-t-2 border-[#0F766E]">
          <div className="bg-[#F6E9DD] border border-[#E0D4C8] p-6">
            <p className="font-mono text-xs uppercase tracking-wider text-[#0F766E] mb-2">
              {article.url ? "Original Source" : "Source"}
            </p>
            <p className="font-serif text-lg text-[#33302E] mb-4">
              {article.url ? (
                <>
                  This article was originally published by{" "}
                  <span className="font-semibold text-[#0F766E]">{article.source}</span>
                </>
              ) : (
                <>
                  Content sourced from{" "}
                  <span className="font-semibold text-[#0F766E]">{article.source}</span>
                </>
              )}
            </p>
            {article.url ? (
              <a
                href={article.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#0F766E] text-white font-semibold hover:bg-[#064E3B] transition-colors"
                data-testid="read-full-story-btn"
              >
                <span>Read Full Story on {article.source}</span>
                <ArrowSquareOut size={18} weight="bold" />
              </a>
            ) : (
              <p className="text-sm text-[#6EE7B7]/80">
                Full article available through {article.source}.
              </p>
            )}
            <p className="text-xs text-[#6EE7B7]/70 mt-4" data-testid="licence-note">
              {article.source === "Gov.ie Agriculture"
                ? "Contains Irish Public Sector Information licensed under a Creative Commons Attribution 4.0 International (CC BY 4.0) licence."
                : article.source === "USDA FSA"
                ? "Source material is a work of the U.S. Government (public domain)."
                : "Farmwatch aggregates news from trusted agricultural sources."}
            </p>
          </div>
        </div>
        )}

        {/* Bottom Leaderboard Ad */}
      </main>
      
      {/* Sidebar - Desktop Only */}
      <aside className="hidden lg:block w-[300px] flex-shrink-0">
      </aside>
        </div>
      </div>
      <Footer />
    </div>
  );
}
